import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { MessageSquare, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DISMISS_KEY_PREFIX = 'exit_survey_banner_dismissed_until_';

const readDismissedUntil = (id) => {
  if (!id) return 0;
  return parseInt(localStorage.getItem(`${DISMISS_KEY_PREFIX}${id}`) || '0', 10);
};

export default function ExitSurveyBanner() {
  const [pending, setPending] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const res = await axios.get(`${API}/exit/my-pending-survey`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const resignation = res.data?.has_pending ? (res.data.resignation || {}) : null;
        if (resignation?.id && readDismissedUntil(resignation.id) > Date.now()) {
          setPending(null);
          setDismissed(true);
        } else {
          setPending(resignation);
          setDismissed(false);
        }
      } catch (err) {
        if (!cancelled) setPending(null);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [location.pathname]);

  if (!pending || dismissed) return null;
  // Hide on the exit-surveys page itself (they are already there)
  if (location.pathname === '/exit-surveys') return null;

  const handleDismiss = () => {
    // Dismiss for 24 hours
    localStorage.setItem(
      `${DISMISS_KEY_PREFIX}${pending.id}`,
      String(Date.now() + 24 * 60 * 60 * 1000)
    );
    setDismissed(true);
  };

  const lwd = pending.last_working_day
    ? new Date(pending.last_working_day).toLocaleDateString()
    : null;

  return (
    <div
      data-testid="exit-survey-banner"
      className="mb-4 rounded-lg border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 dark:border-amber-700 px-4 py-3 flex items-start justify-between gap-3 shadow-sm"
    >
      <div className="flex items-start gap-3">
        <div className="bg-amber-200 dark:bg-amber-800 rounded-full p-2 flex-shrink-0">
          <MessageSquare size={18} className="text-amber-800 dark:text-amber-100" />
        </div>
        <div>
          <p className="font-bold text-amber-900 dark:text-amber-100 text-sm">
            Your resignation has been approved. Please complete your exit survey.
          </p>
          <p className="text-xs text-amber-800/80 dark:text-amber-200/80 mt-0.5">
            Your feedback is confidential and helps us improve.
            {lwd && <> Last working day: <span className="font-semibold">{lwd}</span>.</>}
          </p>
          <div className="mt-2 flex items-center gap-2">
            <Link
              to="/exit-surveys"
              data-testid="exit-survey-banner-cta"
              className="inline-flex items-center px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold transition-colors"
            >
              Complete Exit Survey
            </Link>
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        data-testid="exit-survey-banner-dismiss"
        aria-label="Dismiss for 24 hours"
        className="text-amber-700/70 hover:text-amber-900 dark:text-amber-300/70 dark:hover:text-amber-100 transition-colors flex-shrink-0"
      >
        <X size={16} />
      </button>
    </div>
  );
}
